/**
 * EG-42 / AD-3 + AD-4 — a inversão do fluxo de criação.
 *
 * Traduz "templates selecionados" em "estrutura da tela". Antes o gestor digitava
 * o número de pilares, escolhia o pilar no catálogo, digitava o nome dos temas e
 * só então pendurava as questões. Agora ele escolhe templates e a estrutura
 * aparece a partir da classificação que as questões já têm.
 *
 * A lógica vive fora do componente porque é ela que carrega as regras: acumular
 * e fundir por id_pillar sem deduplicar por code (AD-3), e o balde "Sem
 * classificação" (AD-4). Dentro do JSX, testá-la mediria renderização.
 *
 * @param questoes  questões dos templates selecionados. A ordem de saída é a de
 *                  CRIAÇÃO (id_question_template crescente), não a de seleção —
 *                  e é ela que o backend transforma em index global (AD-6).
 * @param pilares   catálogo global (/pillar). Ausente = ainda carregando.
 * @param temas     catálogo global (/theme).
 * @param exigeTema true no periódico (hierarquia pilar > tema > questão), false
 *                  no certificado, cuja estrutura é só pilar. Medido: 754 das
 *                  755 questões de certificação não têm tema, então exigir tema
 *                  ali jogaria a tela inteira no balde.
 */
export const derivarEstruturaDeTemplates = ({
  questoes = [],
  pilares,
  temas,
  exigeTema = true,
}) => {
  const catalogoDePilares = new Map(
    (pilares ?? []).map((pilar) => [pilar.id_pillar, pilar])
  );
  const catalogoDeTemas = new Map((temas ?? []).map((tema) => [tema.id_theme, tema]));

  // Deduplica por ID, e só por ID. O mesmo question_template é a mesma linha do
  // molde; dois templates com o mesmo `code` são linhas DIFERENTES, com
  // classificação independente (1.605 casos medidos no dump). Confundir as duas
  // coisas apagaria questão que o gestor selecionou de propósito.
  const vistos = new Set();
  const selecionadas = questoes.filter((questao) => {
    if (!questao || vistos.has(questao.id_question_template)) return false;
    vistos.add(questao.id_question_template);
    return true;
  });

  const semClassificacao = [];
  const porPilar = new Map();

  for (const questao of selecionadas) {
    const pilar = catalogoDePilares.get(questao.id_pillar);
    const tema = catalogoDeTemas.get(questao.id_theme);

    // Pilar fora do catálogo cai no balde: aposentado por soft delete não vem em
    // /pillar, e montar uma aba sem nome é pior que mostrar a questão como não
    // classificada. É o mesmo critério do backend (p.deleted_at IS NULL).
    if (!pilar || (exigeTema && !tema)) {
      semClassificacao.push(questao);
      continue;
    }

    if (!porPilar.has(pilar.id_pillar)) {
      porPilar.set(pilar.id_pillar, {
        ...pilar,
        temas: new Map(),
        // No certificado as questões penduram direto no pilar — não há sanfona.
        questoes: [],
      });
    }

    const agrupado = porPilar.get(pilar.id_pillar);

    if (!exigeTema) {
      agrupado.questoes.push(questao);
      continue;
    }

    if (!agrupado.temas.has(tema.id_theme)) {
      agrupado.temas.set(tema.id_theme, { ...tema, questoes: [] });
    }
    agrupado.temas.get(tema.id_theme).questoes.push(questao);
  }

  // A ordem das QUESTÕES é a de criação — id_question_template crescente.
  //
  // Decisão do dev no teste exploratório: a ordem de seleção do template não diz
  // nada sobre a ordem que o gestor espera ver, e a resposta da API não tem ordem
  // garantida. O id é o único critério estável e já significa "ordem em que a
  // pergunta foi criada no template".
  //
  // Isso vale também para o que é GRAVADO: idsParaGravar percorre esta mesma
  // estrutura, e o backend transforma a posição em index global (AD-6). Ordenar
  // aqui mantém a tela e o dado de acordo.
  const porIdDeCriacao = (a, b) =>
    Number(a.id_question_template) - Number(b.id_question_template);

  // O catálogo define a ordem de exibição de pilares e temas; a ordem em que o
  // gestor escolheu os templates é acidente.
  const porIndiceDoCatalogo = (a, b) =>
    (a.index ?? 0) - (b.index ?? 0) ||
    String(a.name_pillar ?? a.title ?? '').localeCompare(
      String(b.name_pillar ?? b.title ?? '')
    );

  return {
    pilares: [...porPilar.values()]
      .map((pilar) => ({
        ...pilar,
        questoes: [...pilar.questoes].sort(porIdDeCriacao),
        temas: [...pilar.temas.values()]
          .map((tema) => ({
            ...tema,
            questoes: [...tema.questoes].sort(porIdDeCriacao),
          }))
          .sort(porIndiceDoCatalogo),
      }))
      .sort(porIndiceDoCatalogo),
    semClassificacao: [...semClassificacao].sort(porIdDeCriacao),
  };
};

/**
 * Os ids de template na ordem em que devem ser gravados.
 *
 * A ordem importa: o backend usa a posição para o index global (AD-6), e a
 * estrutura derivada é o que o gestor está vendo. Gravar em outra ordem faria a
 * tela discordar do que foi salvo.
 */
export const idsParaGravar = (estrutura) => {
  const dosPilares = (estrutura?.pilares ?? []).flatMap((pilar) => [
    ...(pilar.questoes ?? []),
    ...(pilar.temas ?? []).flatMap((tema) => tema.questoes ?? []),
  ]);

  return [...dosPilares, ...(estrutura?.semClassificacao ?? [])].map(
    (questao) => questao.id_question_template
  );
};
